/**
 * A real conversation with the assistant about one machine.
 *
 * WHY THIS EXISTS
 * Reported: "No conversation history when using AI assist on a device." The
 * device page offered one-shot buttons — diagnose, recommend — and nothing
 * else. There was no way to ask a follow-up, and the backend chat endpoint that
 * could have supported one was never wired to any screen.
 *
 * THE HISTORY LIVES ON THE SERVER
 * Not in this component's state. That is the whole point of the fix: navigating
 * away, refreshing, or opening the device in another tab must not throw away
 * what the assistant has been told. The component renders whatever the server
 * says the thread is, and every reply returns the updated thread, so there is
 * one source of truth rather than a local copy that drifts.
 *
 * COMMANDS RUN FROM HERE
 * The assistant proposes PowerShell; the technician reviews it and runs it in
 * place, and the output comes back into the same conversation for the assistant
 * to interpret. Sending someone to another screen to execute what they just
 * asked for is how a two-minute job becomes a five-minute one — and they lose
 * the context that made the proposal make sense.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";
import { formatDateTime } from "../utils/datetime";

type ChatMessage = {
  id: number;
  role: string;
  content: string;
  action_id: number | null;
  meta: Record<string, any>;
  created_at: string;
};

type Props = {
  deviceId: number;
  /** Admin-only: running a command on a machine is not a technician action. */
  canRun?: boolean;
};

/** Split a reply into prose and the PowerShell it proposed, so the code renders
 *  as code and the explanation reads as prose. */
function splitReply(text: string): { prose: string; code: string | null } {
  const m = /```(?:powershell|ps1?|pwsh)?\s*\n([\s\S]*?)```/i.exec(text || "");
  if (!m) return { prose: text || "", code: null };
  const prose = (text.slice(0, m.index) + text.slice(m.index + m[0].length)).trim();
  return { prose, code: m[1].trim() || null };
}

export default function DeviceAIChat({ deviceId, canRun = false }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  /** Command id → live status, for a run started from this thread. */
  const [running, setRunning] = useState<Record<number, string>>({});
  /** Facts the assistant recorded on the last turn, shown so retention is never
   *  something a technician discovers after the fact. */
  const [learned, setLearned] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/v1/ai/devices/${deviceId}/chat`);
      setMessages(r.data?.messages || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not load the conversation");
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Keep the newest turn in view. Only when there is something to scroll to,
    // so an empty panel does not jump the page on mount.
    if (messages.length) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, busy]);

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;
    setError("");
    setBusy(true);
    // Shown immediately with a negative id so it cannot collide with a real
    // row; the server's copy replaces it when the reply lands. Waiting for the
    // round trip to show what you just typed feels broken.
    setMessages((m) => [
      ...m,
      {
        id: -Date.now(),
        role: "user",
        content: q,
        action_id: null,
        meta: {},
        created_at: new Date().toISOString(),
      },
    ]);
    setInput("");
    try {
      const r = await api.post(`/api/v1/ai/devices/${deviceId}/chat`, { message: q });
      setMessages(r.data?.messages || []);
      // Shown, not hidden. The assistant quietly retaining something a
      // technician said is exactly the behaviour people object to when they
      // find out about it later.
      setLearned(r.data?.learned || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "The assistant could not answer that.");
      // The server rolled the question back, so drop the optimistic row rather
      // than leaving a question on screen that no longer exists server-side.
      setMessages((m) => m.filter((x) => x.id > 0));
    } finally {
      setBusy(false);
    }
  };

  const runCommand = async (command: string) => {
    if (!window.confirm(`Run this on the device as SYSTEM?\n\n${command}`)) return;
    setError("");
    try {
      const r = await api.post(`/api/v1/ai/devices/${deviceId}/chat/run`, { command });
      const cmdId = r.data?.command_id;
      if (!cmdId) return;
      setRunning((s) => ({ ...s, [cmdId]: "queued" }));
      poll(cmdId, command);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not queue that command.");
    }
  };

  /** Poll for output, then hand it back to the assistant to interpret — the
   *  loop that makes this a conversation rather than a command box. */
  const poll = (commandId: number, command: string) => {
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await api.get(`/api/v1/devices/${deviceId}/command/${commandId}`);
        const st = r.data?.status;
        setRunning((s) => ({ ...s, [commandId]: st }));
        if (st === "completed" || st === "failed") {
          const out = [r.data?.stdout, r.data?.stderr].filter(Boolean).join("\n").trim();
          setBusy(true);
          try {
            const a = await api.post(`/api/v1/ai/devices/${deviceId}/chat/assess`, {
              command,
              output: out || "(no output)",
              messages: messages
                .filter((m) => m.role === "user" || m.role === "assistant")
                .map((m) => ({ role: m.role, content: m.content })),
            });
            // The verdict is a normal turn, so it survives a reload like any
            // other — reading "did that work?" only in a toast would lose it.
            if (a.data?.reply) {
              await api.post(`/api/v1/ai/devices/${deviceId}/chat`, {
                message: `[Ran] ${command}\n\nOutput:\n${(out || "(no output)").slice(0, 4000)}`,
              });
            }
            await load();
          } finally {
            setBusy(false);
          }
          return;
        }
      } catch {
        /* keep polling — a transient error is not a failed command */
      }
      // ~2 minutes, matching the agent's own command timeout.
      if (tries < 60) setTimeout(tick, 2000);
    };
    setTimeout(tick, 1500);
  };

  const clear = async () => {
    if (!window.confirm("Start a new conversation about this device?")) return;
    try {
      await api.delete(`/api/v1/ai/devices/${deviceId}/chat`);
      setMessages([]);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not clear the conversation");
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
          Ask about this device
        </span>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={clear}
            className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
          >
            New conversation
          </button>
        )}
      </div>

      <div ref={scrollRef} className="max-h-80 overflow-y-auto px-3 py-3 space-y-3">
        {loading && (
          <div className="text-xs text-gray-400 dark:text-gray-500">Loading conversation…</div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Ask anything about this machine — "why is it rebooting at night?", "what's using
            the disk?", "restart the print spooler". The conversation is kept, so you can pick
            it up later.
          </div>
        )}

        {messages.map((m) => {
          if (m.role === "user") {
            return (
              <div key={m.id} className="flex justify-end">
                <div className="max-w-[85%] rounded-lg bg-blue-600 text-white px-3 py-2 text-sm whitespace-pre-wrap">
                  {m.content}
                </div>
              </div>
            );
          }
          const { prose, code } = splitReply(m.content);
          return (
            <div key={m.id} className="flex justify-start">
              <div className="max-w-[92%] space-y-2">
                {prose && (
                  <div className="rounded-lg bg-gray-100 dark:bg-gray-700/50 text-gray-800 dark:text-gray-200 px-3 py-2 text-sm whitespace-pre-wrap">
                    {prose}
                  </div>
                )}
                {code && (
                  <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-500/10 overflow-hidden">
                    <pre className="px-3 py-2 text-[11px] font-mono text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre-wrap">
                      {code}
                    </pre>
                    <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-t border-violet-200 dark:border-violet-800">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        Runs as SYSTEM on this device
                      </span>
                      {canRun ? (
                        <button
                          type="button"
                          onClick={() => runCommand(code)}
                          className="text-[11px] bg-violet-600 hover:bg-violet-700 text-white px-2.5 py-1 rounded"
                        >
                          Run it
                        </button>
                      ) : (
                        <span className="text-[10px] text-gray-400">Admins can run this</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="text-[10px] text-gray-400 dark:text-gray-500">
                  {formatDateTime(m.created_at)}
                </div>
              </div>
            </div>
          );
        })}

        {Object.entries(running).map(([id, st]) =>
          st === "completed" || st === "failed" ? null : (
            <div key={id} className="text-[11px] text-gray-500 dark:text-gray-400 italic">
              Command {id} is {st}…
            </div>
          ),
        )}
        {learned.length > 0 && (
          <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-500/10 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-semibold mb-1">
              Remembered for next time
            </div>
            <ul className="text-xs text-emerald-900 dark:text-emerald-200 space-y-0.5">
              {learned.map((f, i) => (
                <li key={i}>· {f}</li>
              ))}
            </ul>
            <a
              href="/ai-settings"
              className="text-[10px] text-emerald-700 dark:text-emerald-300 underline"
            >
              Review or correct in AI Settings
            </a>
          </div>
        )}
        {busy && <div className="text-xs text-gray-400 italic">Thinking…</div>}
      </div>

      {error && (
        <p className="px-3 pb-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-end gap-2 border-t border-gray-200 dark:border-gray-700 p-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          rows={2}
          placeholder="Ask about this device…"
          className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm resize-none"
        />
        <button
          type="button"
          onClick={() => send(input)}
          disabled={busy || !input.trim()}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg"
        >
          Send
        </button>
      </div>
    </div>
  );
}
